/**
 * Flujo de estados de los módulos previsionales simples.
 *
 * Hay dos variantes, según si el módulo pasa por un turno en ANSES:
 *
 *   Con turno ANSES (Jubilación, PUAM, Plan de Pago, Reconocimiento, PNC, Pensión):
 *     Ingreso del Trámite → Turno Anses → Seguimiento de Expediente → Cerrado favorable
 *                                                                   → Cerrado desfavorable
 *
 *   Sin turno ANSES (Amparo PNC):
 *     Ingreso del Trámite → Cerrado favorable
 *                         → Cerrado desfavorable
 *
 * Además, desde CUALQUIER estado vivo se puede pasar a 'Cancelado' (V208), y desde 'Cancelado'
 * se puede volver al ingreso. Cancelar no es lo mismo que cerrar desfavorable: desfavorable
 * significa que el organismo lo denegó; cancelado, que el trámite no siguió (carga errónea,
 * desistimiento). Mezclarlos ensucia los reportes.
 *
 * Los dos estados del medio los mueve el backend solo (TurnoAnsesService.moverEstadoTramite):
 * 'Turno Anses' al crear el turno y 'Seguimiento de Expediente' cuando la presentación vuelve
 * INGRESADO. Igual se ofrecen como transición manual, porque un trámite puede haberse
 * presentado sin que el turno se cargara en el sistema.
 *
 * Reajuste, Ganancias, Minusvalía y Declaratoria de Herederos NO pasan por acá: cada uno tiene su
 * propio ciclo de estados y su propio mecanismo de transición (TRANSICIONES_POR_ESTADO +
 * SeccionAvanzarEstado, en su propia página). Minusvalía sí pasa por ANSES (Turno Anses lo saca
 * TarjetaTurnoAnses), pero no por este flujo genérico: su turno no cierra el trámite, lo cierra el
 * dictamen de la junta médica. Declaratoria es judicial (fuero provincial) y nunca pasa por ANSES
 * (V233). RTI y ART tampoco: usan sus gestores dedicados con dropdown libre.
 */

// Ámbitos cuyo ciclo incluye la ida a ANSES (los que V140 sembró).
export const AMBITOS_CON_TURNO_ANSES = [
    'JUBILACION',
    'PUAM',
    'PLAN_PAGO',
    'RECONOCIMIENTO',
    'PNC',
    'PENSION',
];

// Amparo PNC comparte el modal guiado pero no los estados intermedios.
//
// AMPARO (el amparo por mora ante ANSES, V239) NO entra acá: tiene ciclo propio de 8 estados y su
// propio mini-formulario por transición en la ficha (SeccionAvanzarEstado de
// VistaDetalladaTramiteAmparo), igual que Declaratoria/Ganancias/Minusvalía. Sumarlo a esta lista
// le daría el modal genérico además del suyo.
//
// GENERICO entra solo para poder cancelar: su ciclo propio (Ingreso → En trámite → Finalizado)
// NO está modelado acá, así que de su catálogo lo único que el modal va a ofrecer es 'Cancelado'
// (y volver al ingreso desde ahí). Es a propósito: sin esto un trámite genérico cargado por error
// no tenía ninguna salida, y esos trámites están destinados a migrarse a un tipo específico.
export const AMBITOS_FLUJO_SIMPLE = [...AMBITOS_CON_TURNO_ANSES, 'AMPARO_PNC', 'GENERICO'];

/**
 * Clasifica un estado por su nombre. Se resuelve por substring y no por id porque los ids los
 * asigna la secuencia de la tabla `estado` al correr la migración: son distintos en cada base
 * (dev, producción) y hardcodearlos rompería en cuanto cambie el orden de inserción.
 *
 * OJO con el orden de los chequeos: 'favorable' es substring de 'desfavorable', así que el
 * desfavorable tiene que evaluarse primero o todo cierre se clasificaría como favorable.
 *
 * 'nulidad' es propio de Pensión (V232, reclamo administrativo sobre una denegatoria): el nombre
 * de estado 'Nulidad Iniciada' nunca va a aparecer en el catálogo de ningún otro ámbito de los
 * que comparten este archivo, así que clasificarlo acá no afecta a Jubilación/PUAM/Plan de
 * Pago/Reconocimiento/PNC.
 */
export function tipoEstado(nombre) {
    const normalizado = String(nombre || '').toLowerCase();

    if (normalizado.includes('cancelado')) return 'cancelado';
    if (normalizado.includes('desfavorable')) return 'desfavorable';
    if (normalizado.includes('favorable')) return 'favorable';
    if (normalizado.includes('nulidad')) return 'nulidad';
    if (normalizado.includes('seguimiento')) return 'seguimiento';
    if (normalizado.includes('turno anses')) return 'turno';
    return 'ingreso';
}

/**
 * Un trámite en un cierre ya terminó: no ofrece más transiciones por el camino normal.
 * 'cancelado' cuenta como cierre (no se le cargan más turnos ni se lo sigue), pero a diferencia
 * de los otros dos sí ofrece volver atrás — ver transicionesPermitidas.
 */
export function esEstadoFinal(nombre) {
    const tipo = tipoEstado(nombre);
    return tipo === 'favorable' || tipo === 'desfavorable' || tipo === 'cancelado';
}

// Camino normal de cada variante. El destino de un estado es lo que sigue en su lista.
const SIGUIENTE_CON_TURNO = {
    ingreso: ['turno'],
    turno: ['seguimiento'],
    seguimiento: ['favorable', 'desfavorable'],
    // Nulidad Iniciada (Pensión, V232): desde acá sigue el mismo circuito que un ingreso normal.
    nulidad: ['turno'],
};

const SIGUIENTE_SIN_TURNO = {
    ingreso: ['favorable', 'desfavorable'],
};

/**
 * Devuelve los destinos válidos desde el estado actual, ya resueltos contra el catálogo real.
 *
 * @param {Array<{id:number, nombre:string, ambito:string}>} estadosDelAmbito
 *        Estados de GET /api/estados ya filtrados por ámbito.
 * @param {string} nombreEstadoActual
 * @param {string} ambito  Define si el ciclo incluye la ida a ANSES.
 * @returns {Array<{idEstado:number, nombre:string, tipo:string}>}
 *        Vacío si el trámite está en un cierre o si el catálogo todavía no cargó.
 */
export function transicionesPermitidas(estadosDelAmbito, nombreEstadoActual, ambito) {
    const catalogo = Array.isArray(estadosDelAmbito) ? estadosDelAmbito : [];
    const actual = tipoEstado(nombreEstadoActual);

    const mapa = AMBITOS_CON_TURNO_ANSES.includes(ambito)
        ? SIGUIENTE_CON_TURNO
        : SIGUIENTE_SIN_TURNO;

    // Cancelado es la única salida reversible: un trámite cancelado por error vuelve al ingreso
    // (mismo criterio que la reactivación que RTI ya tenía). Los cierres favorable/desfavorable
    // siguen sin vuelta atrás por este camino.
    if (actual === 'cancelado') {
        // Se busca por el nombre y no por tipoEstado() === 'ingreso' a propósito: 'ingreso' es el
        // valor por defecto de esa función, así que cualquier estado que no clasifique caería ahí
        // y podría ganarle al verdadero estado de ingreso según el orden del catálogo.
        const ingreso = catalogo.find((e) => String(e.nombre || '').toLowerCase().includes('ingreso'));
        return ingreso ? [{ idEstado: ingreso.id, nombre: ingreso.nombre, tipo: 'ingreso' }] : [];
    }

    // Cancelar está disponible desde cualquier estado vivo, no solo desde el ingreso: el trámite
    // se puede caer en cualquier punto (el cliente desiste, se detecta que estaba mal cargado).
    const tiposDestino = esEstadoFinal(nombreEstadoActual)
        ? []
        : [...(mapa[actual] || []), 'cancelado'];

    // Se mapea el `id` del EstadoDto a `idEstado`, que es lo que espera el body de
    // POST /tramites/{id}/estados.
    return tiposDestino
        .map((tipo) => {
            const estado = catalogo.find((e) => tipoEstado(e.nombre) === tipo);
            return estado ? { idEstado: estado.id, nombre: estado.nombre, tipo } : null;
        })
        .filter(Boolean);
}
